import type { Lead } from '@/types';

// Lead endpoints live on the dataProvider (searchLeads, getLead, getLeadLogs,
// getLeadSource, parseLeadIntake, …). This module keeps the shared error helpers
// and option label used by consumers and by the provider methods.

export function parseError(data: unknown, fallback: string): string {
    if (!data || typeof data !== 'object') return fallback;
    const obj = data as Record<string, unknown>;
    const detail = obj.detail ?? obj.errors;
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object') {
        const root = (detail as Record<string, unknown>).root;
        if (typeof root === 'string') return root;
        if (root && typeof root === 'object' && root !== null && 'serverError' in root) {
            return String((root as { serverError: unknown }).serverError);
        }
    }
    return fallback;
}

export function leadApiErrorMessage(error: unknown, fallback: string): string {
    const err = error as { body?: unknown };
    if (err?.body) {
        const fromBody = parseError(err.body, '');
        if (fromBody) return fromBody;
    }
    if (error instanceof Error && error.message) return error.message;
    return fallback;
}

export type LeadParseDraft = {
    source_text: string;
    source_path: string | null;
    source_paths?: string[] | null;
    lead: {
        name: string;
        email: string;
        phone_number: string;
        requirements: string;
    };
    next_steps: Array<{
        name: string;
        notify: boolean;
        notify_at: string | null;
    }>;
};

export function leadOptionLabel(lead: Lead): string {
    const name = (lead.name || '').trim() || `Lead #${lead.id}`;
    const email = (lead.email || '').trim();
    return email ? `${name} <${email}>` : name;
}

export type LeadPropertyAddress = {
    address: string;
    city: string;
    state: string;
    zip_code: string;
};
